import React, { useEffect, useState } from "react";
import { FieldWrapper, FieldActionWrapper, SelectField } from "@factorearth/component-library";
import { FieldInstance, FieldTemplate, Option } from "db/definitions";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import Svg from "../../Svg/Svg";
import { recordSentry } from "utils/recordSentry";

interface CheckboxFieldProps {
	color: Colors;
	fieldInstance: FieldInstance;
	fieldTemplate: FieldTemplate;
	value: string;
	fromTable?: boolean;
	moreHorizon?: JSX.Element;
	handleChange: (event: { target: { value: string } }) => void;
}

const CheckboxField = (props: CheckboxFieldProps) => {
	const { color, fieldInstance, fieldTemplate, value, handleChange, fromTable, moreHorizon } = props;
	const [values, setValues] = useState<Record<string, Option> | undefined>(undefined);
	const [sortedOptions, setSortedOptions] = useState<Option[]>([]);

	useEffect(() => {
		if (!fieldTemplate.options?.items || !fieldTemplate.optionsOrder) return;
		const optionMap: Map<string, Option> = new Map();
		for (const option of fieldTemplate.options.items) {
			optionMap.set(option.id, option);
		}
		const options: Option[] = [];
		for (let i = 0; i < fieldTemplate.optionsOrder.length; i++) {
			const optionId = fieldTemplate.optionsOrder[i];
			const optionData = optionMap.get(optionId);
			if (optionData) {
				options.push(optionData);
			} else {
				recordSentry({
					error: new Error(
						`CheckboxField: Option with id ${optionId} not found in options map. FieldTemplate: ${fieldTemplate.id} FormTemplate: ${fieldTemplate.formTemplate}, Field: ${fieldInstance.id} (${fieldTemplate.header})`,
					),
					msg: "CheckboxField option not found",
				});
			}
		}
		setSortedOptions(options);
	}, []);

	useEffect(() => {
		const optionIds = value.split(String.fromCharCode(31));
		const optionMap: Map<string, Option> = new Map();
		for (const option of sortedOptions) {
			optionMap.set(option.id, option);
			// If this is a linked checkbox, add the linkedId to the map, since the actual option instance id is not used
			if (option.linkedId) optionMap.set(option.linkedId, option);
		}
		const selectedOptions: Record<string, Option> = {};
		optionIds.forEach((optionId) => {
			// optionId is either the uuid or the linkedId
			const option = optionMap.get(optionId);
			if (option) selectedOptions[optionId] = option;
		});
		setValues(selectedOptions);
	}, [value, sortedOptions]);

	const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// Find the option from the sorted options where the uuid === the event id, or the option's linkedId === event target id
		const newOption = sortedOptions.find((option) => option.id === event.target.id || option.linkedId === event.target.id);
		if (!newOption) return;
		let newValue = {
			...values,
			[event.target.id]: newOption,
		};
		if (values && !event.target.checked && values[event.target.id]) {
			delete values[event.target.id];
			delete newValue[event.target.id];
			setValues({ ...values });
		} else {
			setValues({ ...values, ...newValue });
		}
		const data = {
			target: { value: Object.keys(newValue).join(String.fromCharCode(31)) },
		};
		handleChange(data);
	};

	return (
		<FieldWrapper
			colorPalette={color}
			label={!fromTable ? fieldTemplate.header : undefined}
			link={fieldInstance.altInstanceId ? <Svg icon="LINKED" /> : undefined}
			data-fieldtemplateid={fieldTemplate.id}>
			<FieldActionWrapper>
				<SelectField
					handleChange={handleCheckBoxChange}
					values={values}
					colorPalette={color}
					moreHorizon={moreHorizon ? moreHorizon : undefined}
					options={sortedOptions}
					type="checkbox"
					label={fieldTemplate.header}
				/>
			</FieldActionWrapper>
		</FieldWrapper>
	);
};

export default CheckboxField;
